import { formatToDollar, formatToPercent } from "@inrev/common";
import { SuretyBondPreview } from "@portal/api/bond/agent";
import { checkboxFilter } from "@portal/components/ui/FilterableDataTable";
import {
	bondStatusDisplayLabelMap,
	getStatusTagByBondStatus,
} from "@portal/components/ui/StatusTag";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { bondTypeLabelMap } from "../../../constants";
import { formatDateForCsv, formatEmpty } from "../../../utils";

export const getBondTableColumns = (
	isMinimalUIEnabled: boolean,
): ColumnDef<SuretyBondPreview>[] => [
	{
		id: "status",
		accessorFn: (row) => bondStatusDisplayLabelMap[row.status] ?? row.status,
		header: "Status",
		size: 120,
		minSize: 120,
		filterFn: checkboxFilter,
		cell: (cell) => getStatusTagByBondStatus(cell.row.original.status),
		meta: { headerFilterType: "checkbox" },
	},
	{
		id: "type",
		header: "Type",
		size: 77,
		minSize: 77,
		accessorFn: ({ type }) => bondTypeLabelMap[type],
		filterFn: checkboxFilter,
		meta: { headerFilterType: "checkbox" },
	},
	{
		id: "bondNumber",
		accessorKey: "number",
		header: "Bond Number",
		size: 180,
		minSize: 180,
	},
	{
		id: "accountName",
		accessorFn: ({ accountName }) => formatEmpty(accountName),
		header: "Account",
		size: 180,
		minSize: 180,
		filterFn: checkboxFilter,
		meta: { headerFilterType: "checkbox" },
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 130,
		minSize: 130,
		cell: (context) => formatEmpty(formatToDollar(context.getValue<string>())),
	},
	{
		id: "rate",
		header: "Rate",
		size: 130,
		minSize: 130,
		accessorFn: ({ rate }) => rate ?? 0,
		cell: (context) => formatToPercent(context.row.original.rate),
	},
	{
		id: "premium",
		accessorKey: "premium",
		header: "Premium",
		size: 130,
		minSize: 130,
		cell: (context) => formatToDollar(context.getValue<string>(), true),
	},
	{
		id: "principalName",
		accessorFn: ({ principalName }) => formatEmpty(principalName),
		header: "Principal",
		size: 180,
		minSize: 130,
		filterFn: checkboxFilter,
		meta: { headerFilterType: "checkbox" },
	},
	{
		id: "obligeeName",
		accessorKey: "obligeeName",
		header: "Obligee",
		size: 200,
		minSize: 150,
	},
	{
		id: "description",
		accessorFn: ({ description, name }) => formatEmpty(isMinimalUIEnabled ? name : description),
		header: isMinimalUIEnabled ? "Project Name" : "Description",
		size: 200,
		minSize: 150,
	},
	{
		id: "effectiveDate",
		accessorFn: ({ effectiveDate }) => DateTime.fromISO(effectiveDate).toJSDate(),
		cell: (props) =>
			DateTime.fromISO(props.row.original.effectiveDate).toLocaleString(DateTime.DATE_MED),
		header: "Effective Date",
		size: 150,
		minSize: 130,
		enableGlobalFilter: false,
		meta: { csvFormatter: formatDateForCsv },
	},
	{
		id: "expectedCompletionDate",
		accessorFn: ({ expectedCompletionDate }) =>
			expectedCompletionDate ? DateTime.fromISO(expectedCompletionDate).toJSDate() : undefined,
		cell: (props) =>
			formatEmpty(
				props.row.original.expectedCompletionDate
					? DateTime.fromISO(props.row.original.expectedCompletionDate).toLocaleString(
							DateTime.DATE_MED,
						)
					: props.row.original.expectedCompletionDate,
			),
		size: 200,
		minSize: 130,
		sortingFn: "datetime",
		header: "Est. Completion Date",
		meta: { csvFormatter: formatDateForCsv },
	},
];

export const getAccountViewBondTableColumns = (isMinimalUIEnabled: boolean) =>
	getBondTableColumns(isMinimalUIEnabled).filter((column) => column.id !== "accountName");
